// Internal modules
import { appStore } from '@/store';
import { CARD_BRANDS, CARD_CONFIRMATION_METHODS } from '../constants';
import {
  CROSS_BORDER_ERROR,
  features,
  getContent,
  getGlobals,
  isRtlLang,
  logger,
  noop,
} from '@/lib';
import { getCardMeta, getInstallments } from './api';
import type { CardDefinition } from '@/generated/graphql';
import { InstrumentClassification } from '@/generated/graphql';

/**
 * @function getCardIconPosition
 * @param {object} cardType
 * @param {bool} requiresType
 * @returns {string|null}
 * @description Gets the icon position for the card art and CVV icon. For
 * RTL langs we display the card art on the left side of the input. For LTR
 * langs we show it on the right side. If the card type is not available and
 * it is required then we don't show the card art. If the second parameter,
 * `requiresType`, is passed as `false` then we return the position even if
 * the `cardType` is not available.
 */
const getCardIconPosition = (
  cardType: string,
  requiresType = true,
): null | 'left' | 'right' => {
  if (!cardType && requiresType) {
    return null;
  }

  return isRtlLang() ? 'left' : 'right';
};

const conditionallyFetchInstallments = (): Promise<void> | undefined => {
  const { form: formState } = appStore.getState();
  const { cardNumber } = formState;

  clearAndHideInstallments();

  if (!cardNumber.isValid || !features.isActive('isInstallmentsEligible')) {
    return;
  }

  // If the API call fails we continue on with the flow without
  // showing the installment options.
  return getInstallments()?.then(updateInstallmentData).catch(noop);
};

const updateInstallmentData = (): void => {
  const { api: apiState } = appStore.getState();
  const { installmentData } = apiState;
  const { form: formActions, ui: uiActions } = appStore.getActions();
  const { updateNonAddressField } = formActions;
  const { setIsInstallmentButtonShown } = uiActions;

  const numInstallments = installmentData.length;
  if (numInstallments > 1) {
    return setIsInstallmentButtonShown(true);
  }

  if (numInstallments > 0) {
    updateNonAddressField({
      field: 'installmentTerm',
      payload: {
        displayError: false,
        error: '',
        errorCode: '',
        isValid: true,
        value: installmentData[0].term,
      },
    });
  }

  return setIsInstallmentButtonShown(false);
};

const clearAndHideInstallments = () => {
  const { api: apiActions, ui: uiActions } = appStore.getActions();
  const { setInstallmentData } = apiActions;
  const { setIsInstallmentButtonShown } = uiActions;

  setIsInstallmentButtonShown(false);
  clearSelectedInstallmentTerm();
  setInstallmentData([]);
};

const clearSelectedInstallmentTerm = () => {
  const { form: formActions } = appStore.getActions();
  const { updateNonAddressField } = formActions;
  // clear installment selection when user re-enters card number
  updateNonAddressField({
    field: 'installmentTerm',
    payload: {
      displayError: false,
      error: '',
      errorCode: 'valueMissing',
      isValid: false,
      value: '',
    },
  });
};

/**
 * @function isCupDebitCard
 * @param {object} card | { brand, classifications } - Card object
 * returned from card definitions query.
 * @returns {boolean}
 */
const isCupDebitCard = ({
  brand,
  classifications,
}: CardDefinition): boolean => {
  return (
    brand === CARD_BRANDS.CHINA_UNION_PAY &&
    classifications.includes(InstrumentClassification.DebitCard)
  );
};

/**
 * @function isCupMultiBrandCard
 * @param {array} cardDefinitions
 * @returns {boolean}
 */
const isCupMultiBrandCard = (
  cardDefinitions: CardDefinition[] = [],
): boolean => {
  return (
    cardDefinitions.length > 1 &&
    cardDefinitions.some(({ brand }) => brand === CARD_BRANDS.CHINA_UNION_PAY)
  );
};

/**
 * @function isCardConfirmationMethodOtp
 * @param {array} cardDefinitions
 * @returns {boolean}
 */
const isCardConfirmationMethodOtp = (
  cardDefinitions: CardDefinition[] = [],
): boolean => {
  return cardDefinitions.some(({ confirmationMethods = [] }) =>
    confirmationMethods.includes(
      CARD_CONFIRMATION_METHODS.ONE_TIME_PASSWORD_CONFIRMATION,
    ),
  );
};

const clearFormCardFields = () => {
  const { form: formActions } = appStore.getActions();
  const { updateNonAddressField } = formActions;
  // clear card cvv and expiry as they are hidden on the ui.
  updateNonAddressField({
    field: 'cardCvv',
    payload: {
      displayError: false,
      error: '',
      errorCode: 'valueMissing',
      isValid: false,
      value: '',
    },
  });
  updateNonAddressField({
    field: 'cardExpiry',
    payload: {
      displayError: false,
      error: '',
      errorCode: 'valueMissing',
      isValid: false,
      value: '',
    },
  });
};

const conditionallyFetchCardMeta = (
  e: React.ChangeEvent<HTMLInputElement>,
  value: string,
): Promise<void> | void | undefined => {
  const cobrands =
    getGlobals()?.initialGqlData?.checkoutSession?.merchant.cobrands || [];

  const blockNonDomesticCards = features.isActive('blockNonDomesticCards');
  const shouldDetectCupCardType = features.isActive('shouldDetectCupCardType');

  // We only need to fetch the card definitions when
  // 1. CUP multi-brand or CUP debit cards are enabled.
  // 2. blockNonDomesticCards flag is set.
  const shouldGetCardDefinitions =
    shouldDetectCupCardType || blockNonDomesticCards;
  const shouldGetCardCobrand = cobrands.length > 0;

  if (!shouldGetCardDefinitions && !shouldGetCardCobrand) {
    return;
  }

  const { form: formState, ui: uiState } = appStore.getState();
  const { form: formActions, ui: uiActions } = appStore.getActions();
  const { cardType, value: cardNumber } = formState.cardNumber;
  const { hasCardTypeSelector } = uiState;
  const oldFirstNineDigits = cardNumber.substring(0, 9);
  const newFirstNineDigits = value.substring(0, 9);
  const shouldResetCardFields =
    value.length < 9 || oldFirstNineDigits !== newFirstNineDigits;

  if (shouldResetCardFields) {
    if (shouldGetCardDefinitions) {
      // Reset CVV and expiry fields set by CUP debit case.
      const { cvv, dob, expiry } = uiState.cardFieldsToDisplay;
      if (!cvv && !expiry) {
        logger.info('show_cvv_and_expiry_fields');
        uiActions.setCardFieldsToDisplay({ cvv: true, dob, expiry: true });
      }

      // Reset card type selector for CUP multi-brand case.
      if (hasCardTypeSelector) {
        logger.info('cup_clear_multi_brand_card');
        uiActions.setMultiBrandCardTypes([]);
        uiActions.setHasCardTypeSelector(false);
      }
    }

    if (shouldGetCardCobrand) {
      logger.info('clear_card_cobrand');
      formActions.updateNonAddressField({
        field: 'cardNumber',
        payload: { cobrand: '' },
      });
    }

    // Reset one time password confirmation message.
    const { hasCupOneTimePasswordConfirmationMessage } = uiState;
    if (hasCupOneTimePasswordConfirmationMessage) {
      logger.info('clear_card_confirmation_otp_message');
      uiActions.setHasCupOneTimePasswordConfirmationMessage(false);
    }
  }

  // The card-definitions API requires 9 digits to return reliable results.
  if (newFirstNineDigits.length < 9) {
    return logger.info('skip_fetch_card_meta_call', { cardType });
  }

  // We only use the first nine digits, even when more are available, so that
  // subsequent calls will hit the Apollo cache.
  return getCardMeta(
    newFirstNineDigits,
    shouldGetCardCobrand,
    shouldGetCardDefinitions,
  )
    .then(({ data }) => {
      const { cardMeta } = data;
      if (shouldGetCardDefinitions) {
        const definitions = cardMeta.definitions as CardDefinition[];
        if (!definitions) {
          logger.info('card_definitions_response_empty');
          return;
        }

        // The first element in the array is the primary card.
        const [primaryCard] = definitions;

        if (blockNonDomesticCards) {
          const {
            api: {
              cartDetails: {
                merchant: { country: merchantCountry },
              },
            },
          } = appStore.getState();
          if (primaryCard?.countryCode !== merchantCountry) {
            const issuerCountry = primaryCard?.countryCode;
            const countryName = getContent(`country.${merchantCountry}`);
            // Setting isValid to prevent Form submission
            // when the user has entered non-MX card.
            const error = getContent('messages.blockNonDomesticCard', {
              merchantCountry: countryName,
            }) as unknown as string;
            formActions.updateNonAddressField({
              field: 'cardNumber',
              payload: {
                displayError: true,
                error,
                errorCode: CROSS_BORDER_ERROR.NON_DOMESTIC_CARD,
                isValid: issuerCountry === merchantCountry,
              },
            });
          }
        }

        if (primaryCard && isCupDebitCard(primaryCard)) {
          // CVV and expiry are not required for CUP debit cards
          logger.info('hide_cvv_and_expiry_fields_for_cup_debit');
          const { dob } = uiState.cardFieldsToDisplay;
          uiActions.setCardFieldsToDisplay({ cvv: false, dob, expiry: false });
          clearFormCardFields();
        }

        if (definitions && isCupMultiBrandCard(definitions)) {
          const multiBrandCardTypes = definitions.map(({ brand }) => brand);

          logger.info('cup_set_multi_brand_card');
          uiActions.setMultiBrandCardTypes(multiBrandCardTypes);
          uiActions.setHasCardTypeSelector(true);
        }

        if (isCardConfirmationMethodOtp(definitions)) {
          logger.info('set_card_confirmation_otp_message');
          uiActions.setHasCupOneTimePasswordConfirmationMessage(true);
        }
      }

      if (shouldGetCardCobrand) {
        const { cobrand } = cardMeta;
        if (cobrand) {
          logger.info('card_cobrand', { cobrand });
          formActions.updateNonAddressField({
            field: 'cardNumber',
            payload: { cobrand },
          });
        }
      }
    })
    .catch(noop);
};

export {
  conditionallyFetchCardMeta,
  conditionallyFetchInstallments,
  getCardIconPosition,
  isCardConfirmationMethodOtp,
  isCupDebitCard,
  isCupMultiBrandCard,
  updateInstallmentData,
};
